/**
 * Contratação / checkout — re-exporta wire schemas de `@imobdesk/contracts`
 * e expõe mappers usados pelo `contratacaoService`.
 */
import { z } from 'zod'
import { auth, contratacao, planos, usuarios } from '@imobdesk/contracts'
import { getAttribution } from '@/lib/attribution'

// ---------------------------------------------------------------------------
// Planos públicos
// ---------------------------------------------------------------------------

export const publicPlanoSchema = planos.publicPlanoSchema
export const publicPlanosListSchema = planos.publicPlanosListSchema
export type PlanoPublico = z.infer<typeof publicPlanoSchema>

// ---------------------------------------------------------------------------
// Checkout / contratar (auth)
// ---------------------------------------------------------------------------

export const contratacaoRequestSchema = auth.contratarRequestSchema
export type ContratacaoRequest = z.infer<typeof contratacaoRequestSchema>

export const contratacaoPreviewRequestSchema = auth.contratarPreviewSchema
export type ContratacaoPreviewRequest = z.infer<
  typeof contratacaoPreviewRequestSchema
>

export const contratacaoResponseSchema = auth.contratarCheckoutResponseSchema
export type ContratacaoResponse = z.infer<typeof contratacaoResponseSchema>

export const contratacaoPreviewResponseSchema =
  auth.contratarPreviewResponseSchema
export type ContratacaoPreviewResponse = z.infer<
  typeof contratacaoPreviewResponseSchema
>

export const checkoutSuccessResponseSchema = auth.checkoutSuccessResponseSchema
export type CheckoutSuccessResponse = z.infer<
  typeof checkoutSuccessResponseSchema
>

export const checkoutTargetTokenResolveResponseSchema =
  auth.checkoutTargetTokenResolveResponseSchema

// ---------------------------------------------------------------------------
// OTP guest checkout (inline em contratarRequest.otpCode)
// ---------------------------------------------------------------------------

export { validateOtpFormSchema } from './auth.schema'
export type { ValidateOtpForm } from './auth.schema'

export const currentUserRequestSchema = auth.currentUserRequestSchema
export const currentUserResponseSchema = auth.currentUserResponseSchema

// ---------------------------------------------------------------------------
// Workspace / dashboard no checkout
// ---------------------------------------------------------------------------

export const contratacaoDashboardOptionSchema =
  contratacao.contratacaoDashboardOptionSchema
export type ContratacaoDashboardOption = z.infer<
  typeof contratacaoDashboardOptionSchema
>

export const contratacaoDashboardsResponseSchema =
  contratacao.contratacaoDashboardsResponseSchema

export const dashboardPlanoAtualSchema = planos.planoCompletoSchema
export type DashboardPlanoAtual = z.infer<typeof dashboardPlanoAtualSchema>

export const ativarPlanoRequestSchema = planos.ativarRequestSchema
export type DashboardAtivarPlanoRequest = z.infer<
  typeof ativarPlanoRequestSchema
>

export const ativarPlanoResponseSchema = planos.ativarResponseSchema
export type DashboardAtivarPlanoResponse = z.infer<
  typeof ativarPlanoResponseSchema
>

export const hasCapabilityResponseSchema = usuarios.hasCapabilityResponseSchema

/** POST /dashboard/:id/plano/ativar — bare ou envelope `{ data }`. */
export const ativarPlanoDashboardWireSchema = z.union([
  planos.ativarResponseSchema,
  z.object({ data: planos.ativarResponseSchema }),
])

export type CheckoutTargetResolveData = {
  valid: boolean
  dashboardId?: number
  reason?: string
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

export function withAttribution(dados: ContratacaoRequest): ContratacaoRequest {
  const attribution = getAttribution()
  return contratacaoRequestSchema.parse({
    ...dados,
    gaClientId: dados.gaClientId ?? attribution.gaClientId,
    gaSessionId: dados.gaSessionId ?? attribution.gaSessionId,
    utmSource: dados.utmSource ?? attribution.utmSource,
    utmMedium: dados.utmMedium ?? attribution.utmMedium,
    utmCampaign: dados.utmCampaign ?? attribution.utmCampaign,
    utmContent: dados.utmContent ?? attribution.utmContent,
    utmTerm: dados.utmTerm ?? attribution.utmTerm,
    gclid: dados.gclid ?? attribution.gclid,
    fbclid: dados.fbclid ?? attribution.fbclid,
    pageLocation: dados.pageLocation ?? attribution.pageLocation,
    pageReferrer: dados.pageReferrer ?? attribution.pageReferrer,
    userAgent: dados.userAgent ?? attribution.userAgent,
  })
}

export function mapAtivarPlanoRequestWithAttribution(
  dados: DashboardAtivarPlanoRequest,
): DashboardAtivarPlanoRequest & {
  gaClientId?: string
  gaSessionId?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmTerm?: string
  gclid?: string
  fbclid?: string
  pageLocation?: string
  pageReferrer?: string
} {
  const attribution = getAttribution()
  return {
    ...dados,
    gaClientId: attribution.gaClientId,
    gaSessionId: attribution.gaSessionId,
    utmSource: attribution.utmSource,
    utmMedium: attribution.utmMedium,
    utmCampaign: attribution.utmCampaign,
    utmContent: attribution.utmContent,
    utmTerm: attribution.utmTerm,
    gclid: attribution.gclid,
    fbclid: attribution.fbclid,
    pageLocation: attribution.pageLocation,
    pageReferrer: attribution.pageReferrer,
  }
}

export function unwrapAtivarPlanoDashboardResponse(
  wire: unknown,
): DashboardAtivarPlanoResponse {
  const parsed = ativarPlanoDashboardWireSchema.parse(wire)
  if (parsed && typeof parsed === 'object' && 'data' in parsed) {
    return parsed.data
  }
  return parsed
}

export function unwrapCheckoutTargetResolve(
  wire: unknown,
): CheckoutTargetResolveData {
  const parsed = checkoutTargetTokenResolveResponseSchema.parse(wire)
  if (parsed.data.valid) {
    return { valid: true, dashboardId: parsed.data.dashboardId }
  }
  return { valid: false, reason: parsed.data.reason }
}
